import React, { useState, useRef, useCallback } from 'react';
import {
  X, Target, Clock, Dumbbell, Loader2,
  Brain, Flag, Flame, Circle, CircleDot,
  Video, Upload, Link2, Trash2, ExternalLink,
  MessageSquare, AlertCircle, CheckCircle2, PlayCircle,
  Zap, Bookmark, Play,
} from 'lucide-react';
import { parseISO, formatDistanceToNow, isPast, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { PlayerExerciseAssignment, Exercise, ExerciseCategory } from '@/types/coaching.types';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { getExerciseCategoryImage } from '@/components/coaching/constants';
import { IOSSheet, IOSSheetClose } from '@/components/ui/ios-sheet';

// ---- Design tokens ----
const C = {
  slate900: '#0F172A',
  slate700: '#334155',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  primaryGreen: '#1B4332',
  secondaryGreen: '#2D5A3D',
  brightGreen: '#52B788',
  primaryBlue: '#2A4494',
  deepBlue: '#1E3270',
  warningAmber: '#D97706',
  errorRed: '#DC2626',
};

const STYLES: Record<string, React.CSSProperties> = {
  heroBranded: {
    background: `linear-gradient(135deg, ${C.brightGreen} 0%, ${C.secondaryGreen} 50%, ${C.primaryGreen} 100%)`,
    boxShadow: '0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 20px rgba(27,67,50,0.20)',
  },
  closeChip: {
    background: 'rgba(255, 255, 255, 0.18)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  listCard: {
    background: '#FFFFFF',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.05)',
  },
  iconBubbleNeutral: {
    background: 'rgba(15, 23, 42, 0.06)',
    border: '1px solid rgba(15, 23, 42, 0.10)',
  },
  iconBubbleBlue: {
    background: `linear-gradient(135deg, ${C.primaryBlue} 0%, ${C.deepBlue} 100%)`,
    boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 2px 6px rgba(42,68,148,0.24)',
  },
  primaryCTA: {
    background: `linear-gradient(135deg, ${C.brightGreen} 0%, ${C.secondaryGreen} 50%, ${C.primaryGreen} 100%)`,
    boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px rgba(27,67,50,0.22)',
  },
  videoCTA: {
    background: `linear-gradient(135deg, ${C.primaryBlue} 0%, ${C.deepBlue} 100%)`,
    boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px rgba(42,68,148,0.24)',
  },
  videoChip: {
    background: 'rgba(42, 68, 148, 0.08)',
    border: '1px solid rgba(42, 68, 148, 0.28)',
  },
  videoIconBubble: {
    background: 'rgba(42, 68, 148, 0.12)',
    border: '1px solid rgba(42, 68, 148, 0.28)',
  },
  videoDashed: {
    background: C.slate50,
    border: '1.5px dashed rgba(42, 68, 148, 0.42)',
  },
  videoActionChip: {
    background: 'rgba(42, 68, 148, 0.10)',
    border: '1px solid rgba(42, 68, 148, 0.28)',
  },
  deleteChip: {
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.22)',
  },
  searchInput: {
    background: C.slate50,
    border: '1px solid rgba(15, 23, 42, 0.10)',
    color: C.slate900,
  },
  glassChip: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)',
  },
  alertOverdue: {
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.22)',
  },
  alertUrgent: {
    background: 'rgba(217, 119, 6, 0.08)',
    border: '1px solid rgba(217, 119, 6, 0.22)',
  },
  alertNormal: {
    background: C.slate100,
    border: '1px solid rgba(15, 23, 42, 0.08)',
  },
  completedCard: {
    background: 'rgba(82, 183, 136, 0.10)',
    border: '1px solid rgba(82, 183, 136, 0.32)',
  },
  amberDot: {
    background: 'rgba(217, 119, 6, 0.12)',
    border: '1px solid rgba(217, 119, 6, 0.28)',
  },
  progressTrack: {
    background: C.slate100,
  },
  overrideBanner: {
    background: 'rgba(217, 119, 6, 0.08)',
    border: '1px solid rgba(217, 119, 6, 0.22)',
  },
  overrideRing: {
    border: '1.5px solid rgba(217, 119, 6, 0.42)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(217, 119, 6, 0.10)',
  },
  overridePill: {
    background: 'rgba(217, 119, 6, 0.10)',
    border: '1px solid rgba(217, 119, 6, 0.32)',
    color: '#92400E',
  },
  noteTextarea: {
    background: C.slate50,
    border: '1px solid rgba(15, 23, 42, 0.10)',
    color: C.slate900,
    boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset',
  },
};

// ---- Helpers ----

function getCategoryIcon(category: ExerciseCategory): React.ElementType {
  switch (category) {
    case 'putting': return Circle;
    case 'short_game': return Flag;
    case 'driving': return CircleDot;
    case 'iron_play': return Target;
    case 'bunker': return Flag;
    case 'mental': return Brain;
    case 'fitness': return Dumbbell;
    case 'warm_up': return Flame;
    default: return CircleDot;
  }
}

interface DueStatusTranslations {
  noDueDate: string;
  overdue: string;
  dueToday: string;
  dueTomorrow: string;
  dueIn: string;
}

function getDueStatus(dueDate: string | null, translations: DueStatusTranslations) {
  if (!dueDate) return { text: translations.noDueDate, urgency: 'normal' as const };
  const due = parseISO(dueDate);
  const daysUntil = differenceInDays(due, new Date());
  if (isPast(due)) return { text: translations.overdue, urgency: 'overdue' as const };
  if (daysUntil === 0) return { text: translations.dueToday, urgency: 'urgent' as const };
  if (daysUntil === 1) return { text: translations.dueTomorrow, urgency: 'urgent' as const };
  return { text: `${translations.dueIn} ${daysUntil} days`, urgency: 'normal' as const };
}

const Eyebrow: React.FC<{ children: React.ReactNode; accentDot?: React.CSSProperties; accentColor?: string }> = ({
  children, accentDot, accentColor,
}) => (
  <div className="flex items-center gap-2 px-1">
    {accentDot && <span className="w-4 h-4 rounded-md inline-flex" style={accentDot} />}
    <p className="text-[11px] font-bold uppercase" style={{ color: accentColor || C.slate500, letterSpacing: '0.12em' }}>
      {children}
    </p>
  </div>
);

// ---- Props ----

type AssignedProps = {
  mode: 'assigned';
  assignment: PlayerExerciseAssignment & {
    duration_override?: number | null;
    repetitions_override?: string | null;
    equipment_override?: string[] | null;
    instructions_override?: string | null;
    target_override?: string | null;
  };
  onComplete: (id: string) => void;
  onStart: (id: string) => void;
  isCompleting: boolean;
  isStarting: boolean;
  onSubmitVideo: (id: string, url: string, playerNote?: string) => Promise<void>;
  onRemoveVideo: (id: string) => Promise<void>;
  isSubmittingVideo: boolean;
  isRemovingVideo: boolean;
  onAskCoach?: (assignment: PlayerExerciseAssignment) => void;
  isAskingCoach?: boolean;
};

type PreviewProps = {
  mode: 'preview';
  exercise: Exercise;
  onAdd: (exercise: Exercise) => void;
  isSaved?: boolean;
  addLabel?: string;
};

type ExerciseDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the sheet has slid away; clear the shown item here. */
  onClosed?: () => void;
} & (AssignedProps | PreviewProps);

// ---- Component ----

export const ExerciseDetailModal: React.FC<ExerciseDetailModalProps> = (props) => {
  const { isOpen, onClose } = props;
  const { t } = useAppTranslations();
  const navigate = useNavigate();

  const [videoMode, setVideoMode] = useState<'none' | 'link' | 'upload'>('none');
  const [linkInput, setLinkInput] = useState('');
  const [playerNote, setPlayerNote] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadVideo, uploading: isUploading, uploadProgress } = useMediaUpload();

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.mode !== 'assigned') return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) return;
    const result = await uploadVideo(file, { folder: 'coaching/exercise-videos', maxSize: 100 * 1024 * 1024 });
    if (result?.url) {
      await props.onSubmitVideo(props.assignment.id, result.url, playerNote.trim() || undefined);
      setVideoMode('none');
      setPlayerNote('');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [props, uploadVideo, playerNote]);

  const handleLinkSubmit = useCallback(async () => {
    if (props.mode !== 'assigned') return;
    if (!linkInput.trim()) return;
    await props.onSubmitVideo(props.assignment.id, linkInput.trim(), playerNote.trim() || undefined);
    setLinkInput('');
    setPlayerNote('');
    setVideoMode('none');
  }, [props, linkInput, playerNote]);

  // Resolve exercise from either mode
  const exercise = props.mode === 'assigned' ? props.assignment.exercise : props.exercise;

  // Derived values for assigned mode
  const assignment = props.mode === 'assigned' ? props.assignment : null;
  const displayDuration = assignment?.duration_override ?? exercise.duration_mins;
  const displayReps = assignment?.repetitions_override ?? exercise.repetitions;
  const displayEquipment = assignment?.equipment_override ?? exercise.equipment_needed;
  const hasOverrides = assignment
    ? !!(assignment.duration_override || assignment.repetitions_override || assignment.equipment_override || assignment.target_override || assignment.instructions_override)
    : false;

  const status = assignment?.status;
  const isCompleted = status === 'completed';
  const isInProgress = status === 'in_progress';

  const categoryLabels: Record<ExerciseCategory, string> = {
    putting: (t.coaching?.player?.skillPutting || 'PUTTING').toUpperCase(),
    short_game: (t.coaching?.player?.skillShortGame || 'SHORT GAME').toUpperCase(),
    driving: (t.coaching?.player?.skillDriving || 'DRIVING').toUpperCase(),
    iron_play: (t.coaching?.player?.skillIronPlay || 'IRON PLAY').toUpperCase(),
    bunker: (t.coaching?.player?.skillBunker || 'BUNKER').toUpperCase(),
    mental: (t.coaching?.player?.skillMental || 'MENTAL').toUpperCase(),
    fitness: (t.coaching?.exerciseLibrary?.categoryFitness || 'FITNESS').toUpperCase(),
    warm_up: (t.coaching?.exerciseLibrary?.categoryWarmUp || 'WARM UP').toUpperCase(),
  };
  const categoryLabel = categoryLabels[exercise.category] || exercise.category.toUpperCase();

  const dueStatus = getDueStatus(assignment?.due_date ?? null, {
    noDueDate: t.coaching?.player?.noDueDate || 'No due date',
    overdue: t.coaching?.player?.overdue || 'Overdue',
    dueToday: t.coaching?.player?.dueToday || 'Due today',
    dueTomorrow: t.coaching?.player?.dueTomorrow || 'Due tomorrow',
    dueIn: t.coaching?.player?.dueIn || 'Due in',
  });

  const alertVariant =
    dueStatus.urgency === 'overdue'
      ? { card: STYLES.alertOverdue, icon: C.errorRed, text: C.errorRed }
      : dueStatus.urgency === 'urgent'
      ? { card: STYLES.alertUrgent, icon: C.warningAmber, text: C.warningAmber }
      : { card: STYLES.alertNormal, icon: C.slate500, text: C.slate700 };

  // Preview mode values
  const isSaved = props.mode === 'preview' ? (props.isSaved ?? false) : false;
  const addLabel = props.mode === 'preview' ? props.addLabel : undefined;

  // Pinned under the scrolling content: start/complete/ask-coach, or save/play in preview.
  const footer =
    props.mode === 'preview' || !isCompleted ? (
      <div className="space-y-2.5">
        {props.mode === 'assigned' && !isCompleted && (
          <>
            <button
              onClick={() => (isInProgress ? props.onComplete(props.assignment.id) : props.onStart(props.assignment.id))}
              disabled={props.isCompleting || props.isStarting}
              className="w-full h-12 rounded-xl text-white text-[14px] font-semibold tracking-tight flex items-center justify-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-50"
              style={STYLES.primaryCTA}
            >
              {props.isCompleting || props.isStarting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isInProgress ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  {t.coaching?.player?.markComplete || 'Mark Complete'}
                </>
              ) : (
                <>
                  <PlayCircle className="w-4 h-4" />
                  {t.coaching?.player?.startExercise || 'Start Exercise'}
                </>
              )}
            </button>
            {props.onAskCoach && props.assignment.coach?.user_id && (
              <button
                onClick={() => props.onAskCoach!(props.assignment)}
                disabled={props.isAskingCoach}
                className="w-full h-11 rounded-xl text-[13px] font-semibold tracking-tight flex items-center justify-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-50"
                style={{ ...STYLES.glassChip, color: C.slate700 }}
              >
                {props.isAskingCoach ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4" style={{ color: C.primaryBlue }} />
                    {t.coaching?.player?.askCoach || 'Ask Coach'}
                  </>
                )}
              </button>
            )}
          </>
        )}

        {props.mode === 'preview' && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => { props.onAdd(exercise as Exercise); onClose(); }}
              disabled={isSaved}
              className="active:scale-[0.98] transition-transform"
              style={{
                flex: 1, height: 48, borderRadius: '14px', fontSize: '13px', fontWeight: '700',
                color: isSaved ? '#027A48' : '#344054',
                border: `1.5px solid ${isSaved ? 'rgba(6,174,100,0.25)' : 'rgba(15,23,42,0.10)'}`,
                background: isSaved ? 'rgba(6,174,100,0.07)' : 'rgba(15,23,42,0.04)',
                cursor: isSaved ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}
            >
              <Bookmark style={{ width: 14, height: 14, strokeWidth: 2.5 }} />
              {isSaved ? t.coaching.myPractice.saved : (addLabel || t.coaching.myPractice.save)}
            </button>
            <button
              onClick={() => { onClose(); navigate(`/practice/${exercise.id}`, { state: { exercise } }); }}
              className="active:scale-[0.98] transition-transform"
              style={{
                flex: 1, height: 48, borderRadius: '14px', fontSize: '13px', fontWeight: '700',
                color: 'white', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                ...STYLES.primaryCTA,
              }}
            >
              <Play style={{ width: 14, height: 14, fill: 'white', strokeWidth: 0 }} />
              {t.coaching.myPractice.play}
            </button>
          </div>
        )}
      </div>
    ) : undefined;

  return (
    <IOSSheet
      open={isOpen}
      onOpenChange={(open) => { if (!open) onClose(); }}
      onClosed={props.onClosed}
      title={exercise.name}
      header={
        <div className="px-4 pb-1">
          <div className="px-4 py-3" style={{ ...STYLES.heroBranded, borderRadius: '20px' }}>
            <div className="flex items-center gap-3">
              <img
                src={exercise.thumbnail_url || getExerciseCategoryImage(exercise.category)}
                alt={exercise.name}
                className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                style={{ border: '1px solid rgba(255,255,255,0.30)' }}
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-[15px] font-extrabold text-white tracking-tight truncate">{exercise.name}</h3>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span
                    className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase text-white"
                    style={{ background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.20)', letterSpacing: '0.10em' }}
                  >
                    {categoryLabel}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase text-white"
                    style={{ background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.20)', letterSpacing: '0.10em' }}
                  >
                    {exercise.difficulty}
                  </span>
                  {isCompleted && (
                    <span
                      className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase text-white flex items-center gap-1"
                      style={{ background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.20)', letterSpacing: '0.10em' }}
                    >
                      <CheckCircle2 className="w-2.5 h-2.5" />
                      {t.coaching?.player?.completed || 'Completed'}
                    </span>
                  )}
                </div>
              </div>
              <IOSSheetClose asChild>
                <button
                  type="button"
                  aria-label={t.common.close}
                  className="tap-target h-9 w-9 rounded-xl flex items-center justify-center active:scale-[0.96] transition-transform flex-shrink-0 text-white"
                  style={STYLES.closeChip}
                >
                  <X className="w-4 h-4" />
                </button>
              </IOSSheetClose>
            </div>
          </div>
        </div>
      }
      footer={footer}
    >
      <div className="pt-4 pb-1 space-y-4">

        {/* Coach-customized banner — assigned mode only */}
        {hasOverrides && (
          <div className="p-3 rounded-xl flex items-center gap-2" style={STYLES.overrideBanner}>
            <Zap className="w-4 h-4 flex-shrink-0" style={{ color: C.warningAmber }} />
            <span className="text-[12px] font-bold tracking-tight" style={{ color: '#92400E' }}>
              {t.coaching?.player?.customizedByCoach || 'Customized by your coach'}
            </span>
          </div>
        )}

        {/* Description */}
        <div className="space-y-2">
          <Eyebrow>{t.coaching?.modals?.description || 'Description'}</Eyebrow>
          <div className="rounded-2xl p-4" style={STYLES.listCard}>
            <p className="text-[13px] font-medium leading-relaxed" style={{ color: C.slate700 }}>
              {exercise.description || 'No description available for this exercise yet.'}
            </p>
          </div>
        </div>

        {/* Custom instructions override — assigned mode only */}
        {assignment?.instructions_override && (
          <div className="space-y-2">
            <Eyebrow accentDot={STYLES.amberDot} accentColor={C.slate500}>
              {t.coaching?.player?.customInstructionsForYou || 'Custom instructions for you'}
            </Eyebrow>
            <div className="rounded-2xl p-4" style={{ ...STYLES.listCard, ...STYLES.overrideRing }}>
              <p className="text-[13px] font-medium leading-relaxed" style={{ color: C.slate700 }}>
                {assignment.instructions_override}
              </p>
            </div>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-2">
          <div
            className="rounded-2xl p-3 flex items-center gap-2.5"
            style={assignment?.duration_override ? { ...STYLES.listCard, ...STYLES.overrideRing } : STYLES.listCard}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={STYLES.iconBubbleNeutral}>
              <Clock className="w-4 h-4" style={{ color: C.slate700 }} />
            </div>
            <div className="min-w-0">
              <p className="text-[16px] font-extrabold tabular-nums tracking-tight leading-none" style={{ color: C.slate900 }}>
                {displayDuration}{' '}
                <span className="text-[11px] font-bold" style={{ color: C.slate500 }}>
                  {t.coaching?.common?.min || 'min'}
                </span>
              </p>
              <p className="text-[11px] font-bold uppercase mt-1 inline-flex items-center gap-1" style={{ color: C.slate500, letterSpacing: '0.10em' }}>
                {t.coaching?.player?.duration || 'Duration'}
                {assignment?.duration_override && <Zap className="w-2.5 h-2.5" style={{ color: C.warningAmber }} />}
              </p>
            </div>
          </div>
          {displayReps && (
            <div
              className="rounded-2xl p-3 flex items-center gap-2.5"
              style={assignment?.repetitions_override ? { ...STYLES.listCard, ...STYLES.overrideRing } : STYLES.listCard}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={STYLES.iconBubbleNeutral}>
                <Dumbbell className="w-4 h-4" style={{ color: C.slate700 }} />
              </div>
              <div className="min-w-0">
                <p className="text-[14px] font-extrabold tracking-tight leading-tight truncate" style={{ color: C.slate900 }}>
                  {displayReps}
                </p>
                <p className="text-[11px] font-bold uppercase mt-1 inline-flex items-center gap-1" style={{ color: C.slate500, letterSpacing: '0.10em' }}>
                  {t.coaching?.player?.reps || 'Reps'}
                  {assignment?.repetitions_override && <Zap className="w-2.5 h-2.5" style={{ color: C.warningAmber }} />}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Coach notes — both modes */}
        {assignment?.coach_notes && (
          <div className="space-y-2">
            <Eyebrow accentDot={STYLES.amberDot} accentColor={C.slate500}>
              {t.coaching?.player?.coachNotes || 'Coach Notes'}
            </Eyebrow>
            <div className="rounded-2xl p-4" style={STYLES.listCard}>
              <p className="text-[13px] font-medium leading-relaxed italic" style={{ color: C.slate700 }}>
                "{assignment.coach_notes}"
              </p>
            </div>
          </div>
        )}

        {/* Equipment */}
        {props.mode === 'assigned' ? (
          displayEquipment && displayEquipment.length > 0 && (
            <div className="space-y-2">
              <Eyebrow accentDot={assignment?.equipment_override ? STYLES.amberDot : undefined} accentColor={C.slate500}>
                {assignment?.equipment_override
                  ? (t.coaching?.player?.customEquipmentForYou || 'Custom equipment for you')
                  : (t.coaching?.player?.equipment || 'Equipment')}
              </Eyebrow>
              <div className="flex flex-wrap gap-1.5">
                {displayEquipment.map((item, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-1 rounded-md text-[11px] font-bold uppercase"
                    style={
                      assignment?.equipment_override
                        ? { ...STYLES.overridePill, letterSpacing: '0.08em' }
                        : { background: 'rgba(15, 23, 42, 0.06)', border: '1px solid rgba(15, 23, 42, 0.10)', color: C.slate700, letterSpacing: '0.08em' }
                    }
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="space-y-2">
            <Eyebrow>{t.coaching?.player?.equipment || 'Equipment'}</Eyebrow>
            {exercise.equipment_needed && exercise.equipment_needed.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {exercise.equipment_needed.map((item, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-1 rounded-md text-[11px] font-bold uppercase capitalize"
                    style={{ background: 'rgba(15, 23, 42, 0.06)', border: '1px solid rgba(15, 23, 42, 0.10)', color: C.slate700, letterSpacing: '0.08em' }}
                  >
                    {item}
                  </span>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl p-4" style={STYLES.listCard}>
                <p className="text-[12px] font-medium italic" style={{ color: C.slate500 }}>
                  No specific equipment required.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Due date alert — assigned mode only, not when completed */}
        {props.mode === 'assigned' && !isCompleted && (
          <div className="p-3 rounded-xl" style={alertVariant.card}>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: alertVariant.icon }} />
              <span className="text-[13px] font-bold tracking-tight" style={{ color: alertVariant.text }}>
                {dueStatus.text}
              </span>
            </div>
          </div>
        )}

        {/* My Video — assigned mode only */}
        {props.mode === 'assigned' && (
          <div className="space-y-2">
            <Eyebrow accentColor={C.primaryBlue}>
              {t.coaching?.player?.myVideo || 'My Video'}
            </Eyebrow>

            {assignment?.player_video_url ? (
              <div className="flex items-center gap-3 p-3 rounded-2xl" style={STYLES.videoChip}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={STYLES.videoIconBubble}>
                  <Video className="w-5 h-5" style={{ color: C.primaryBlue }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold tracking-tight" style={{ color: C.slate900 }}>
                    {t.coaching?.player?.videoShared || 'Video shared with coach'}
                  </p>
                  <p className="text-[11px] font-medium truncate" style={{ color: C.slate500 }}>
                    {assignment.player_video_submitted_at
                      ? formatDistanceToNow(parseISO(assignment.player_video_submitted_at), { addSuffix: true })
                      : t.coaching?.player?.sentToCoach || 'Sent to coach'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <a
                    href={assignment.player_video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-lg flex items-center justify-center active:scale-[0.96] transition-transform"
                    style={STYLES.videoActionChip}
                    aria-label="Open video"
                  >
                    <ExternalLink className="w-3.5 h-3.5" style={{ color: C.primaryBlue }} />
                  </a>
                  <button
                    onClick={() => props.onRemoveVideo(props.assignment.id)}
                    disabled={props.isRemovingVideo}
                    className="w-8 h-8 tap-target rounded-lg inline-flex items-center justify-center shrink-0 active:scale-[0.96] transition-transform disabled:opacity-50"
                    style={STYLES.deleteChip}
                    aria-label="Delete video"
                  >
                    {props.isRemovingVideo ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: C.errorRed }} />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" style={{ color: C.errorRed }} />
                    )}
                  </button>
                </div>
              </div>
            ) : videoMode === 'none' ? (
              <div className="space-y-2.5">
                <p className="text-[12px] font-medium" style={{ color: C.slate500 }}>
                  {t.coaching?.player?.shareForFeedback || 'Share a video for feedback'}
                </p>
                <div>
                  <label className="text-[11px] font-bold uppercase block mb-1.5 px-1" style={{ color: C.slate500, letterSpacing: '0.10em' }}>
                    {t.coaching?.player?.addNoteForCoach || 'Add a note for your coach'}
                  </label>
                  <textarea
                    value={playerNote}
                    onChange={(e) => setPlayerNote(e.target.value)}
                    placeholder={t.placeholders?.addNotes || 'Add notes...'}
                    className="w-full px-3 py-2 text-[13px] font-medium rounded-xl resize-none focus:outline-none"
                    style={STYLES.noteTextarea}
                    rows={2}
                    maxLength={500}
                  />
                  <p className="text-[12px] font-medium mt-0.5 text-right tabular-nums" style={{ color: C.slate400 }}>
                    {playerNote.length}/500
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-3 rounded-2xl text-center active:scale-[0.99] transition-transform"
                    style={STYLES.videoDashed}
                  >
                    <Upload className="w-5 h-5 mx-auto mb-1.5" style={{ color: C.primaryBlue }} />
                    <span className="text-[12px] font-bold tracking-tight block" style={{ color: C.slate900 }}>
                      {t.coaching?.player?.uploadVideo || 'Upload Video'}
                    </span>
                    <span className="text-[12px] font-medium" style={{ color: C.slate500 }}>
                      {t.coaching?.player?.fromCameraRoll || 'From Camera Roll'}
                    </span>
                  </button>
                  <button
                    onClick={() => setVideoMode('link')}
                    className="p-3 rounded-2xl text-center active:scale-[0.99] transition-transform"
                    style={STYLES.videoDashed}
                  >
                    <Link2 className="w-5 h-5 mx-auto mb-1.5" style={{ color: C.primaryBlue }} />
                    <span className="text-[12px] font-bold tracking-tight block" style={{ color: C.slate900 }}>
                      {t.coaching?.player?.pasteLink || 'Paste Link'}
                    </span>
                    <span className="text-[12px] font-medium" style={{ color: C.slate500 }}>YouTube, etc.</span>
                  </button>
                </div>
                <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileSelect} />
              </div>
            ) : videoMode === 'link' ? (
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.slate400 }} />
                    <input
                      type="url"
                      placeholder={t.placeholders?.enterAddress || 'Enter URL'}
                      value={linkInput}
                      onChange={(e) => setLinkInput(e.target.value)}
                      className="w-full pl-9 pr-3 h-11 text-[13px] font-medium rounded-xl focus:outline-none"
                      style={{ ...STYLES.searchInput, boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset' }}
                      autoFocus
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase block mb-1.5 px-1" style={{ color: C.slate500, letterSpacing: '0.10em' }}>
                    {t.coaching?.player?.addNoteForCoach || 'Add a note for your coach'}
                  </label>
                  <textarea
                    value={playerNote}
                    onChange={(e) => setPlayerNote(e.target.value)}
                    placeholder={t.placeholders?.addNotes || 'Add notes...'}
                    className="w-full px-3 py-2 text-[13px] font-medium rounded-xl resize-none focus:outline-none"
                    style={STYLES.noteTextarea}
                    rows={2}
                    maxLength={500}
                  />
                  <p className="text-[12px] font-medium mt-0.5 text-right tabular-nums" style={{ color: C.slate400 }}>
                    {playerNote.length}/500
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleLinkSubmit}
                    disabled={!linkInput.trim() || props.isSubmittingVideo}
                    className="flex-1 h-10 rounded-xl text-white text-[12px] font-bold tracking-tight flex items-center justify-center gap-1.5 active:scale-[0.99] transition-transform disabled:opacity-50"
                    style={STYLES.videoCTA}
                  >
                    {props.isSubmittingVideo ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Video className="w-3.5 h-3.5" />
                        {t.coaching?.player?.shareVideo || 'Share Video'}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => { setVideoMode('none'); setLinkInput(''); setPlayerNote(''); }}
                    className="px-4 h-10 rounded-xl text-[12px] font-bold tracking-tight active:scale-[0.99] transition-transform"
                    style={{ ...STYLES.glassChip, color: C.slate700 }}
                  >
                    {t.coaching?.modals?.cancel || 'Cancel'}
                  </button>
                </div>
              </div>
            ) : null}

            {isUploading && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: C.primaryBlue }} />
                  <span className="text-[12px] font-bold tracking-tight" style={{ color: C.slate900 }}>
                    {t.coaching?.player?.uploadingVideo || 'Uploading video'}
                  </span>
                  <span className="text-[11px] font-semibold tabular-nums ml-auto" style={{ color: C.primaryBlue }}>
                    {uploadProgress}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={STYLES.progressTrack}>
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%`, background: `linear-gradient(90deg, ${C.primaryBlue}, ${C.deepBlue})` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Completed status — assigned mode only */}
        {props.mode === 'assigned' && isCompleted && assignment && (
          <div className="p-3.5 rounded-2xl flex items-center justify-center gap-2" style={STYLES.completedCard}>
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${C.brightGreen} 0%, ${C.secondaryGreen} 100%)`, boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 2px 6px rgba(27,67,50,0.22)' }}
            >
              <CheckCircle2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-[13px] font-bold tracking-tight" style={{ color: C.secondaryGreen }}>
              {t.coaching?.player?.completed || 'Completed'}
            </span>
          </div>
        )}
      </div>
    </IOSSheet>
  );
};

export default ExerciseDetailModal;
